import { useState, useCallback, useEffect, useRef } from "react";
import axios from "../services/axios";

export interface LiveDataStatus {
  live_data_enable: boolean;
  live_data_period: number | null;
}

interface UseLiveDataSessionOptions {
  deviceId: string | undefined;
  autoRefresh?: boolean;
  refreshInterval?: number; 
}

export const useLiveDataSession = ({
  deviceId,
  autoRefresh = true,
  refreshInterval = 5000,
}: UseLiveDataSessionOptions) => {
  const [status, setStatus] = useState<LiveDataStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  const fetchStatus = useCallback(async () => {
    if (!deviceId) return;

    try {
      const response = await axios.get(`/api-v1/device/${deviceId}/live-data/status/`);
      const data: LiveDataStatus = response.data;
      
      setStatus(data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || "Failed to fetch live data status");
    }
  }, [deviceId]);

  const startLiveData = useCallback(
    async (durationMinutes: number = 1) => {
      if (!deviceId) return;

      setLoading(true);
      setError(null);

      try {
        const response = await axios.post(`/api-v1/device/${deviceId}/live-data/start/`, {
          duration_minutes: durationMinutes,
        });

        if (response.data.status === "started") {
          await fetchStatus();
          setLoading(false);
          return true;
        } else {
          setError("Failed to start live data");
          setLoading(false);
          return false;
        }
      } catch (err: any) {
        setError(err.response?.data?.error || err.message || "Failed to start live data");
        setLoading(false);
        return false;
      }
    },
    [deviceId, fetchStatus]
  );

  const stopLiveData = useCallback(async () => {
    if (!deviceId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(`/api-v1/device/${deviceId}/live-data/stop/`);
      if (response.data.status === "stopped") {
        await fetchStatus();
        setLoading(false);
        return true;
      } else {
        setError("Failed to stop live data");
        setLoading(false);
        return false;
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || "Failed to stop live data");
      setLoading(false);
      return false;
    }
  }, [deviceId, fetchStatus]);

  useEffect(() => {
    setStatus(null);
    setError(null);
  }, [deviceId]);

  useEffect(() => {
    fetchStatus();
  }, [deviceId, fetchStatus]);

  useEffect(() => {
    stopPolling();

    const shouldPoll = autoRefresh && deviceId && status?.live_data_enable === true;

    if (shouldPoll) {
      const interval = setInterval(() => {
        fetchStatus();
      }, refreshInterval);

      pollingIntervalRef.current = interval;

      return () => {
        clearInterval(interval);
      };
    }
  }, [autoRefresh, deviceId, refreshInterval, fetchStatus, stopPolling, status?.live_data_enable]);

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return {
    status,
    loading,
    error,
    startLiveData,
    stopLiveData,
    fetchStatus,
    clearError: () => setError(null),
  };
};

